import * as React from "react"
import { ChevronRight, Folder, FolderUp, Loader2, PackageOpen } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"

type FolderEntry = { id: string; name: string; path: string; path_lower: string }
type FolderListResponse = {
  path: string
  current_folder_id: string | null
  folders: FolderEntry[]
  error?: string
  recovered_from?: { path: string; error: string }
}

export function DropboxFolderPicker({
  open,
  onOpenChange,
  initialPath = "",
  onPick,
  title = "Pick a Dropbox folder",
  ctaLabel = "Use this folder",
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialPath?: string
  onPick: (folder: { path: string; id?: string }) => void
  title?: string
  ctaLabel?: string
}) {
  const [path, setPath] = React.useState(initialPath)
  const [currentFolderId, setCurrentFolderId] = React.useState<string | null>(null)
  const [folders, setFolders] = React.useState<FolderEntry[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    void loadFolder(initialPath)
  }, [open, initialPath])

  async function loadFolder(targetPath: string) {
    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      const url = new URL("/settings/dropbox/folders", window.location.origin)
      // Always send the `path` param, even when empty, so the server knows
      // we mean "Dropbox root" rather than "use your default fallback".
      url.searchParams.set("path", targetPath)
      const res = await fetch(url.toString(), {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      })
      const data = (await res.json()) as FolderListResponse
      if (!res.ok) {
        setError(data.error ?? "Couldn't list folders.")
      } else {
        setPath(data.path)
        setCurrentFolderId(data.current_folder_id ?? null)
        setFolders(data.folders)
        if (data.recovered_from) {
          setNotice(`Couldn't open ${data.recovered_from.path || "/"} (${data.recovered_from.error}). Showing the Dropbox root instead.`)
        }
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  function open_(folder: FolderEntry) {
    void loadFolder(folder.path)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
            <button
              type="button"
              title="Dropbox root"
              aria-label="Go to Dropbox root"
              onClick={() => loadFolder("")}
              disabled={!path}
              className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-accent hover:bg-page hover:text-accent-display disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
            >
              <PackageOpen className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Go up one"
              aria-label="Go up one level"
              onClick={() => {
                const parent = path.split("/").slice(0, -1).join("/")
                void loadFolder(parent)
              }}
              disabled={!path}
              className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-ink-muted hover:bg-page hover:text-accent-display disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
            >
              <FolderUp className="h-4 w-4" />
            </button>
            <span className="truncate font-mono text-ink-display" title={path || "/ (Dropbox root)"}>
              {path || "/ (Dropbox root)"}
            </span>
          </div>

          {notice && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              {notice}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto rounded-md border border-hairline">
            {loading && (
              <div className="flex items-center justify-center p-6 text-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm">Loading…</span>
              </div>
            )}
            {!loading && error && (
              <div className="p-4 text-sm text-destructive">{error}</div>
            )}
            {!loading && !error && folders.length === 0 && (
              <div className="p-4 text-sm text-ink-muted">No subfolders here.</div>
            )}
            {!loading && !error && folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => open_(folder)}
                className="flex w-full cursor-pointer items-center justify-between gap-2 border-b border-hairline px-3 py-2 text-left text-sm transition hover:bg-surface"
              >
                <span className="flex items-center gap-2 truncate">
                  <Folder className="h-4 w-4 shrink-0 text-ink-muted" />
                  <span className="truncate">{folder.name}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-ink-muted" />
              </button>
            ))}
          </div>

          <p className="text-xs text-ink-muted">
            Click a folder to navigate in. Click <strong>{ctaLabel}</strong> to use the folder shown above.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={loading}
            onClick={() => {
              onPick({ path: path, id: currentFolderId ?? undefined })
              onOpenChange(false)
            }}
          >
            {ctaLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
